import { delayedPromise, isElement, isHTMLElement } from '../../utils';
import { QueryCallback, TrackedQuery } from './types';

export class TrackedHandler {
    private observer: IntersectionObserver;
    private topElement: Element;
    private query: string;
    private callback: QueryCallback;
    private element: Element;
    private enabled: boolean;

    constructor(observer: IntersectionObserver, topElement: Element, trackedQuery: TrackedQuery) {
        this.observer = observer;
        this.topElement = topElement;
        this.query = trackedQuery.query;
        this.callback = trackedQuery.callback;
        this.enabled = true;

        this.findElement();
    }

    private async findElement() {
        do {
            this.element = this.topElement.querySelector(this.query);

            if (this.element) {
                break;
            }

            await delayedPromise(250);
        } while (this.enabled);

        if (this.element == null || !isElement(this.element)) {
            return; // either no element was found, or the element found is not observable
        }

        if (isHTMLElement(this.element)) {
            this.element.dataset.tracking_id = this.query;
        }

        this.observer.observe(this.element);
    }

    get isEnabled() {
        return this.enabled;
    }

    get trackedElement() {
        return this.element;
    }

    get handlerValid() {
        if (!this.isEnabled) {
            return false;
        }

        if (!this.trackedElement) {
            return false;
        }

        if (!this.observer.root.contains(this.trackedElement)) {
            return false;
        }

        return true;
    }

    handler(observer: IntersectionObserver, entry: IntersectionObserverEntry) {
        if (
            observer === this.observer &&
            this.topElement.contains(this.element) &&
            this.element.isSameNode(entry.target)
        ) {
            const tracking_id = isHTMLElement(this.element) ? this.element.dataset.tracking_id : this.query;

            if (this.callback) {
                if (this.callback(entry, tracking_id)) {
                    this.unobserve();
                }
            }
        }

        return this;
    }

    unobserve() {
        if (this.observer && this.element) {
            this.observer.unobserve(this.element);
        }

        this.element = null;
        this.enabled = false;
        return this;
    }

    observe() {
        this.unobserve();
        this.enabled = true;
        this.findElement();
        return this;
    }
}
